import { Component, ElementRef, ViewChild } from '@angular/core';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faDatabase, faEye, faTrash, faPlus, faPen } from '@fortawesome/free-solid-svg-icons';

import { AlertService } from '../../components/alert-service/alert-service';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { ItemListComponent, ListItem, ListConfig } from '../../components/item-list/item-list.component';
import { ModalDeleteConfirmComponent } from '../../components/modal-delete-confirm/modal-delete-confirm.component';
import { ModalRenameConfirmComponent } from '../../components/modal-rename-confirm/modal-rename-confirm.component';
import { CommonModule } from '@angular/common';
import { BenchmarkService, BenchmarkItem } from '../../services/benchmark.service';
import { Router } from '@angular/router';
import { TenantService } from '../../services/tenant.service';

@Component({
  selector: 'app-evaluation',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    TranslateModule,
    HeaderActionsComponent,
    ItemListComponent,
    ModalDeleteConfirmComponent,
    ModalRenameConfirmComponent
  ],
  templateUrl: './evaluation.component.html',
  styleUrls: ['./evaluation.component.scss']
})
export class BenchmarkComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  
  constructor(
    private benchmarkService: BenchmarkService,
    private translate: TranslateService,
    private alert: AlertService,
    private router: Router,
    private tenantService: TenantService
  ) {}
  
  benchmarks: BenchmarkItem[] = [];
  loading = false;

  showUploadModal = false;
  showDeleteModal = false;
  showRenameModal = false;
  selectedFile: File | null = null;
  benchmarkToDelete: ListItem | null = null;
  benchmarkToRename: ListItem | null = null;

  faDatabase = faDatabase;
  faEye = faEye;
  faTrash = faTrash;

  headerConfig: HeaderActionsConfig = {
    addButton: {
      type: 'add',
      text: 'BUTTON.UPLOAD',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    showAddButton: true,
    showSearch: false,
    showTagFilter: false,
    showBackButton: true,
    backButtonTitle: 'SIDEBAR.SETTINGS'
  };

  listConfig: ListConfig = {
    itemIcon: faDatabase,
    showMetadata: true,
    emptyMessage: 'MESSAGE.NO.BENCHMARKS',
    actions: [
      {
        id: 'view',
        label: 'BUTTON.VIEW',
        icon: faEye,
        cssClass: 'view-item'
      },
      {
        id: 'rename',
        label: 'BUTTON.RENAME',
        icon: faPen,
        cssClass: 'rename-item'
      },
      {
        id: 'delete',
        label: 'BUTTON.DELETE',
        icon: faTrash,
        cssClass: 'delete-item'
      }
    ]
  };

  onFiltersChanged(filters: any) {}

  get benchmarksAsListItems(): ListItem[] {
    return this.benchmarks.map(b => ({
      id: b.id,
      name: b.name,
      created_date: this.formatDate(b.created_at)
    }));
  }

  formatDate(date: string | Date): string {
    if (!date) return '';

    const d = new Date(date);

    const datePart = d.toLocaleDateString('pt-BR');
    const timePart = d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return `${datePart} ${timePart}`;
  }

  onUploadClick() {
    this.fileInput?.nativeElement.click();
  }

  closeUpload() {
    this.showUploadModal = false;
    this.selectedFile = null;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      console.log('Arquivo selecionado:', file);
      this.uploadFile(file);
    }
    input.value = '';
  }

  confirmUpload() {
    if (this.selectedFile) {
      this.uploadFile(this.selectedFile);
      this.closeUpload();
    }
  }

  removeFile() {
    this.selectedFile = null;
  }

  uploadFile(file: File) {
    this.loading = true;

    this.benchmarkService.uploadBenchmark(file).subscribe({
      next: () => {
        this.loadBenchmarks();
      },
      error: (err) => {
        this.loading = false;
        console.error('Erro no upload', err);
      }
    });
  }

  loadBenchmarks() {
    this.loading = true;
    this.benchmarkService.listBenchmarks().subscribe({
      next: (res) => {
        this.benchmarks = res.benchmarks;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error('Erro ao carregar benchmarks', err);
      }
    });
  }

  onItemAction(event: { action: string, item: ListItem }) {
    if (event.action === 'view') {
      this.viewBenchmark(event.item);
      return;
    }

    if (event.action === 'rename') {
      this.renameBenchmark(event.item);
      return;
    }

    if (event.action === 'delete') {
      this.deleteBenchmark(event.item);
    }
  }

  onItemClick(item: ListItem) {
    console.log('Abrir benchmark', item);
  }

  onSearchCleared() {}
  onAllFiltersCleared() {}

  onBack() {
    this.router.navigate(this.tenantService.buildRoute('/settings'));
  }
  
  ngOnInit() {
    this.loadBenchmarks();
  }

  deleteBenchmark(item: ListItem) {
    this.benchmarkToDelete = item;
    this.showDeleteModal = true;
  }

  onDeleteClose() {
    this.showDeleteModal = false;
    this.benchmarkToDelete = null;
  }

  onDeleteConfirm() {
    if (!this.benchmarkToDelete) {
      return;
    }

    this.loading = true;
    this.benchmarkService.deleteBenchmark(this.benchmarkToDelete.id).subscribe({
      next: () => {
        this.onDeleteClose();
        this.loadBenchmarks();
      },
      error: (err) => {
        this.loading = false;
        this.onDeleteClose();
        console.error('Erro ao deletar', err);
      }
    });
  }

  renameBenchmark(item: ListItem) {
    this.benchmarkToRename = item;
    this.showRenameModal = true;
  }

  onRenameClose() {
    if (this.loading) {
      return;
    }
    this.showRenameModal = false;
    this.benchmarkToRename = null;
  }

  onRenameConfirm(trimmedName: string) {
    if (!this.benchmarkToRename) {
      return;
    }

    this.loading = true;
    this.benchmarkService.renameBenchmark(this.benchmarkToRename.id, trimmedName).subscribe({
      next: () => {
        this.loading = false;
        this.alert.showSuccess(this.translate.instant('BENCHMARK.RENAME_SUCCESS'));
        this.onRenameClose();
        this.loadBenchmarks();
      },
      error: (err) => {
        this.loading = false;
        this.alert.showError(err, this.translate.instant('BENCHMARK.RENAME_ERROR'));
      }
    });
  }

  viewBenchmark(item: ListItem) {
    window.open(`/api/evaluation/view/${item.id}`, '_blank');
  }
}
